import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import {
    FiArrowLeft,
    FiSend,
    FiPaperclip,
    FiDownload,
    FiX,
    FiCheckCircle,
    FiClock,
    FiAlertCircle,
    FiXCircle,
    FiUser,
    FiFileText,
} from "react-icons/fi";

// Format date
const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

// Format time
const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

export default function TicketDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [replyText, setReplyText] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Static data - Ticket Detail
    const ticket = {
        id: id || "TKT-001",
        subject: "Account Statement Download Issue",
        category: "Technical",
        priority: "high",
        status: "in_progress",
        createdAt: "2024-01-20T10:30:00",
        assignedTo: "Support Team",
        messages: [
            {
                id: 1,
                sender: "You",
                senderType: "client",
                message: "I'm unable to download my account statement. The download button is not working. Please help.",
                timestamp: "2024-01-20T10:30:00",
                attachments: [
                    { name: "screenshot1.png", size: 245678, type: "image/png" },
                ],
            },
            {
                id: 2,
                sender: "Support Team",
                senderType: "support",
                message: "Thank you for reporting this issue. We have received your ticket and our technical team is looking into it. We'll update you shortly.",
                timestamp: "2024-01-20T11:15:00",
                attachments: [],
            },
            {
                id: 3,
                sender: "Support Team",
                senderType: "support",
                message: "We've identified the issue and applied a fix. Please try downloading your statement again. If the problem persists, please let us know.",
                timestamp: "2024-01-22T14:20:00",
                attachments: [],
            },
        ],
        attachments: [
            { name: "screenshot1.png", size: 245678, type: "image/png", uploadedBy: "You", uploadedAt: "2024-01-20T10:30:00" },
        ],
        statusHistory: [
            { status: "open", timestamp: "2024-01-20T10:30:00", changedBy: "System" },
            { status: "in_progress", timestamp: "2024-01-20T11:15:00", changedBy: "Support Team" },
        ],
    };

    const getStatusConfig = (status) => {
        const configs = {
            open: {
                color: "#ef4444",
                bg: "#fee2e2",
                icon: FiAlertCircle,
                label: "Open",
            },
            in_progress: {
                color: "#f59e0b",
                bg: "#fef3c7",
                icon: FiClock,
                label: "In Progress",
            },
            resolved: {
                color: "#10b981",
                bg: "#d1fae5",
                icon: FiCheckCircle,
                label: "Resolved",
            },
            closed: {
                color: "#6b7280",
                bg: "#f3f4f6",
                icon: FiXCircle,
                label: "Closed",
            },
        };
        return configs[status] || configs.open;
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
        const maxSize = 5 * 1024 * 1024; // 5MB

        const validFiles = files.filter((file) => {
            if (!allowedTypes.includes(file.type)) {
                alert(`File type ${file.type} is not allowed. Only JPEG, PNG, and PDF files are allowed.`);
                return false;
            }
            if (file.size > maxSize) {
                alert(`File ${file.name} exceeds 5MB size limit.`);
                return false;
            }
            return true;
        });

        setAttachments((prev) => [...prev, ...validFiles]);
    };

    const removeAttachment = (index) => {
        setAttachments((prev) => prev.filter((_, i) => i !== index));
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim() && attachments.length === 0) return;

        setIsSubmitting(true);
        // Simulate API call
        setTimeout(() => {
            alert("Reply sent successfully!");
            setReplyText("");
            setAttachments([]);
            setIsSubmitting(false);
        }, 1000);
    };

    const handleCloseRequest = () => {
        if (window.confirm("Are you sure you want to close this ticket? You can reopen it if needed.")) {
            alert("Ticket closure request submitted.");
        }
    };

    const statusConfig = getStatusConfig(ticket.status);

    return (
        <>
            <ClientNavbar />
            <div className="page-content" style={{ background: "#f8fafc" }}>
                    {/* Header */}
                    <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start flex-wrap">
                                <div className="flex-grow-1">
                                    <button
                                        className="btn btn-sm mb-3"
                                        onClick={() => navigate("/support/tickets")}
                                        style={{
                                            borderRadius: "8px",
                                            borderColor: "#b3eaf4",
                                            color: "#006e82",
                                            backgroundColor: "transparent",
                                        }}
                                    >
                                        <FiArrowLeft className="me-2" />
                                        Back to Tickets
                                    </button>
                                    <h3 className="text-primary mb-2">Ticket Detail</h3>
                                    <nav
                                        aria-label="breadcrumb"
                                        style={{ "--bs-breadcrumb-divider": "none" }}
                                    >
                                        <ol className="breadcrumb mb-0">
                                            <li className="breadcrumb-item">
                                                <NavLink to="/overview">Dashboard</NavLink> /
                                            </li>
                                            <li className="breadcrumb-item">
                                                <NavLink to="/support/tickets" >
                                                    Support / Tickets
                                                </NavLink>{" "}
                                                /
                                            </li>
                                            <li className="breadcrumb-item active">{ticket.id}</li>
                                        </ol>
                                    </nav>
                                    {/* <h3 className="mb-2 fw-bold" style={{ color: "#006e82", fontSize: "28px" }}>
                                        {ticket.subject}
                                    </h3> */}
                              
                                </div>
                                <div className="d-flex gap-2 align-items-center flex-wrap mt-2">
                                    <span
                                        className="badge px-3 py-2 d-inline-flex align-items-center fw-semibold"
                                        style={{
                                            fontSize: "12px",
                                            backgroundColor: statusConfig.bg,
                                            color: statusConfig.color,
                                        }}
                                    >
                                        <statusConfig.icon className="me-1" style={{ fontSize: "12px" }} />
                                        {statusConfig.label}
                                    </span>
                                    <button
                                        className="btn btn-primary btn-sm fw-semibold"
                                        onClick={handleCloseRequest}
                                       
                                    >
                                        Close Request
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row g-4">
                        {/* Message Thread */}
                        <div className="col-lg-8">
                            <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                                <div
                                    className="card-header border-0 py-3 px-4"
                                    style={{
                                        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                        borderBottom: "1px solid #b3eaf4",
                                    }}
                                >
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="me-3 d-flex align-items-center justify-content-center"
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                borderRadius: "10px",
                                                background: "#c8eeecbf",
                                            }}
                                        >
                                            <FiFileText style={{ color: "#39b2b4", fontSize: "20px" }} />
                                        </div>
                                        <h5 className="mb-0 fw-bold" style={{ color: "#006e82" }}>
                                            Conversation Thread
                                        </h5>
                                    </div>
                                </div>
                                <div className="card-body p-4" style={{ maxHeight: "600px", overflowY: "auto", backgroundColor: "#f8fafc" }}>
                                    {ticket.messages.map((message) => (
                                        <div
                                            key={message.id}
                                            className={`mb-4 d-flex ${message.senderType === "client" ? "justify-content-end" : "justify-content-start"}`}
                                        >
                                            <div
                                                className="d-inline-block p-4 rounded shadow-sm"
                                                style={{
                                                    maxWidth: "75%",
                                                    backgroundColor: message.senderType === "client" ? "#ccf1f7" : "#ffffff",
                                                    border: `2px solid ${message.senderType === "client" ? "#b3eaf4" : "#e9ecef"}`,
                                                    borderRadius: "12px",
                                                    position: "relative",
                                                }}
                                            >
                                                <div className="d-flex align-items-center mb-3">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center me-2"
                                                        style={{
                                                            width: "32px",
                                                            height: "32px",
                                                            borderRadius: "50%",
                                                            backgroundColor: message.senderType === "client" ? "#39b2b4" : "#006e82",
                                                            color: "white",
                                                        }}
                                                    >
                                                        <FiUser style={{ fontSize: "16px" }} />
                                                    </div>
                                                    <div>
                                                        <strong style={{ color: "#006e82", fontSize: "14px", display: "block" }}>
                                                            {message.sender}
                                                        </strong>
                                                        <span style={{ color: "#64748b", fontSize: "11px" }}>
                                                            {formatDate(message.timestamp)} {formatTime(message.timestamp)}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div style={{ color: "#006e82", fontSize: "14px", lineHeight: "1.7", marginBottom: message.attachments && message.attachments.length > 0 ? "12px" : "0" }}>
                                                    {message.message}
                                                </div>
                                                {message.attachments && message.attachments.length > 0 && (
                                                    <div className="mt-3 pt-3 border-top" style={{ borderColor: message.senderType === "client" ? "#b3eaf4" : "#e9ecef" }}>
                                                        {message.attachments.map((file, index) => (
                                                            <div
                                                                key={index}
                                                                className="d-inline-flex align-items-center p-2 me-2 mb-2 rounded"
                                                                style={{
                                                                    backgroundColor: message.senderType === "client" ? "#ffffff" : "#ccf1f7",
                                                                    border: `1px solid ${message.senderType === "client" ? "#b3eaf4" : "#b3eaf4"}`,
                                                                }}
                                                            >
                                                                <FiPaperclip className="me-2" style={{ color: "#006e82", fontSize: "14px" }} />
                                                                <span style={{ fontSize: "12px", color: "#006e82", fontWeight: "500" }}>{file.name}</span>
                                                                <button
                                                                    className="btn btn-sm ms-2"
                                                                    style={{
                                                                        color: "#39b2b4",
                                                                        background: "transparent",
                                                                        border: "none",
                                                                        padding: "2px 4px",
                                                                    }}
                                                                    title="Download"
                                                                >
                                                                    <FiDownload style={{ fontSize: "14px" }} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Reply Form */}
                                <div className="card-footer border-0 p-4" style={{ backgroundColor: "#ffffff", borderTop: "1px solid #b3eaf4" }}>
                                    <form onSubmit={handleReply}>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "14px" }}>
                                                Your Reply
                                            </label>
                                            <textarea
                                                className="form-control"
                                                rows="4"
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                                placeholder="Type your reply here..."
                                                style={{
                                                    borderRadius: "10px",
                                                    borderColor: "#b3eaf4",
                                                    fontSize: "14px",
                                                    backgroundColor: "#ffffff",
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = "#006e82";
                                                    e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                    e.target.style.backgroundColor = "#ffffff";
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = "#b3eaf4";
                                                    e.target.style.boxShadow = "none";
                                                    e.target.style.backgroundColor = "#ffffff";
                                                }}
                                            />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold mb-2" style={{ color: "#006e82", fontSize: "14px" }}>
                                                Attachments (Optional)
                                            </label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                multiple
                                                accept="image/jpeg,image/png,image/jpg,application/pdf"
                                                onChange={handleFileChange}
                                                style={{
                                                    borderRadius: "10px",
                                                    borderColor: "#b3eaf4",
                                                    fontSize: "13px",
                                                }}
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = "#006e82";
                                                    e.target.style.boxShadow = "0 0 0 0.2rem rgba(0, 110, 130, 0.1)";
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = "#b3eaf4";
                                                    e.target.style.boxShadow = "none";
                                                }}
                                            />
                                            <div className="form-text mt-1" style={{ fontSize: "12px", color: "#64748b" }}>
                                                Allowed file types: JPEG, PNG, PDF. Maximum file size: 5MB per file.
                                            </div>
                                            {attachments.length > 0 && (
                                                <div className="mt-3">
                                                    {attachments.map((file, index) => (
                                                        <div
                                                            key={index}
                                                            className="d-inline-flex align-items-center p-2 me-2 mb-2 rounded"
                                                            style={{
                                                                backgroundColor: "#ccf1f7",
                                                                border: "1px solid #b3eaf4",
                                                            }}
                                                        >
                                                            <FiPaperclip className="me-2" style={{ color: "#006e82", fontSize: "12px" }} />
                                                            <span style={{ fontSize: "12px", color: "#006e82", fontWeight: "500" }}>
                                                                {file.name} ({(file.size / 1024).toFixed(2)} KB)
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm ms-2"
                                                                onClick={() => removeAttachment(index)}
                                                                style={{
                                                                    color: "#ef4444",
                                                                    background: "transparent",
                                                                    border: "none",
                                                                    padding: "2px 4px",
                                                                }}
                                                                title="Remove"
                                                            >
                                                                <FiX style={{ fontSize: "12px" }} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="d-flex justify-content-end">
                                            <button
                                                type="submit"
                                                className="btn fw-semibold"
                                                disabled={isSubmitting || (!replyText.trim() && attachments.length === 0)}
                                                style={{
                                                    borderRadius: "10px",
                                                    background: isSubmitting || (!replyText.trim() && attachments.length === 0)
                                                        ? "#94a3b8"
                                                        : "linear-gradient(135deg, #39b2b4 0%, #06b6a4 100%)",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "10px 24px",
                                                    transition: "all 0.3s ease",
                                                }}
                                            >
                                                <FiSend className="me-2" />
                                                {isSubmitting ? "Sending..." : "Send Reply"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar - Status & Attachments */}
                        <div className="col-lg-4">
                            {/* Ticket Info */}
                            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                                <div
                                    className="card-header border-0 py-3 px-4"
                                    style={{
                                        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                        borderBottom: "1px solid #b3eaf4",
                                    }}
                                >
                                    <h6 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Ticket Information</h6>
                                </div>
                                <div className="card-body p-4">
                                    <div className="mb-3">
                                        <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Ticket ID</div>
                                        <div style={{ color: "#006e82", fontWeight: "600" }}>{ticket.id}</div>
                                    </div>
                                    <div className="mb-3">
                                        <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Category</div>
                                        <span
                                            className="badge px-3 py-2"
                                            style={{
                                                fontSize: "12px",
                                                backgroundColor: "#39b2b4",
                                                color: "white",
                                            }}
                                        >
                                            {ticket.category}
                                        </span>
                                    </div>
                                    <div className="mb-3">
                                        <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Priority</div>
                                        <span
                                            className="badge px-3 py-2"
                                            style={{
                                                fontSize: "12px",
                                                backgroundColor: "#ef444420",
                                                color: "#ef4444",
                                                border: "1px solid #ef444440",
                                            }}
                                        >
                                            High
                                        </span>
                                    </div>
                                    <div className="mb-3">
                                        <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Created</div>
                                        <div style={{ color: "#006e82", fontSize: "13px" }}>
                                            {formatDate(ticket.createdAt)} {formatTime(ticket.createdAt)}
                                        </div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "4px" }}>Assigned To</div>
                                        <div style={{ color: "#006e82", fontSize: "13px" }}>{ticket.assignedTo}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Status Timeline */}
                            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
                                <div
                                    className="card-header border-0 py-3 px-4"
                                    style={{
                                        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                        borderBottom: "1px solid #b3eaf4",
                                    }}
                                >
                                    <h6 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Status Timeline</h6>
                                </div>
                                <div className="card-body p-4">
                                    {ticket.statusHistory.map((history, index) => {
                                        const histStatusConfig = getStatusConfig(history.status);
                                        const HistIcon = histStatusConfig.icon;
                                        return (
                                            <div key={index} className="d-flex align-items-start mb-3">
                                                <div
                                                    className="me-3 d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: "32px",
                                                        height: "32px",
                                                        borderRadius: "50%",
                                                        backgroundColor: histStatusConfig.bg,
                                                        border: `2px solid ${histStatusConfig.color}`,
                                                    }}
                                                >
                                                    <HistIcon style={{ color: histStatusConfig.color, fontSize: "14px" }} />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div style={{ color: "#006e82", fontWeight: "500", fontSize: "13px" }}>
                                                        {histStatusConfig.label}
                                                    </div>
                                                    <div style={{ color: "#64748b", fontSize: "12px" }}>
                                                        {formatDate(history.timestamp)} {formatTime(history.timestamp)}
                                                    </div>
                                                    <div style={{ color: "#64748b", fontSize: "11px" }}>
                                                        by {history.changedBy}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Attachments */}
                            <div className="card shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                                <div
                                    className="card-header border-0 py-3 px-4"
                                    style={{
                                        background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
                                        borderBottom: "1px solid #b3eaf4",
                                    }}
                                >
                                    <h6 className="mb-0 fw-bold" style={{ color: "#006e82" }}>Attachments</h6>
                                </div>
                                <div className="card-body p-4">
                                    {ticket.attachments.length === 0 ? (
                                        <div style={{ color: "#64748b", fontSize: "13px" }}>No attachments</div>
                                    ) : (
                                        ticket.attachments.map((file, index) => (
                                            <div
                                                key={index}
                                                className="d-flex align-items-center justify-content-between p-3 mb-2 rounded"
                                                style={{
                                                    backgroundColor: "#ccf1f7",
                                                    border: "1px solid #b3eaf4",
                                                }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <FiPaperclip className="me-2" style={{ color: "#006e82", fontSize: "16px" }} />
                                                    <div>
                                                        <div style={{ color: "#006e82", fontSize: "13px", fontWeight: "500" }}>
                                                            {file.name}
                                                        </div>
                                                        <div style={{ color: "#64748b", fontSize: "11px" }}>
                                                            {(file.size / 1024).toFixed(2)} KB
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    className="btn btn-sm"
                                                    style={{
                                                        color: "#39b2b4",
                                                        background: "transparent",
                                                        border: "none",
                                                    }}
                                                >
                                                    <FiDownload style={{ fontSize: "16px" }} />
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
        </>
    );
}
